import { FadeIn, SectionHeader } from "../components/Motion";

const BLOCKS = [
  {
    n: "01",
    title: "Lab testing is often priced beyond what SMEs can justify",
    body: [
      "A full laboratory shelf-life study can cost thousands of dollars per product, and take months while samples sit in storage chambers. For an SME launching three products a year, that can add up to tens of thousands of dollars before a single unit sells.",
      "The result: lab testing becomes the gatekeeper of innovation. Products launch late, reformulations stall, and brands pay enterprise prices for answers they needed at concept stage.",
    ],
  },
  {
    n: "02",
    title: "Guessing is the default — and the risk",
    body: [
      "With lab studies out of reach, many brands copy a competitor's date or rely on gut feel. It feels harmless. It isn't.",
      "Under the Food Standards Code, date marking is a legal requirement for most packaged food. A date you can't justify is a compliance risk on every unit you sell.",
    ],
  },
  {
    n: "03",
    title: "Expertise is out of reach",
    body: [
      "The alternative — hiring a full-time food scientist — costs more annually than some growing brands can justify, especially when shelf-life questions only arise a handful of times a year.",
    ],
  },
];

const Problem = () => (
  <section id="problem" data-testid="problem-section" className="relative py-28 sm:py-36">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeader
        index="01"
        label="The Problem"
        title="Shelf life is one of the costliest guesses in your business."
        lead="Every food product carries a date that decides its safety, its retail life, and its legal standing. Yet for most food businesses, that date is either bought at laboratory prices — or guessed."
      />

      <div className="mt-20 grid grid-cols-1 gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <FadeIn className="lg:sticky lg:top-32">
            <div className="spotlight-frame overflow-hidden rounded-[2rem]">
              <img
                src="https://images.unsplash.com/photo-1486825586573-7131f7991bdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxmb29kJTIwc2NpZW5jZSUyMGxhYm9yYXRvcnklMjByZXNlYXJjaHxlbnwwfHx8fDE3OTEwMjY4MTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Predictive microbiology sample testing in a food science laboratory"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105"
                data-testid="problem-image"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0812] via-[#0B0812]/60 to-transparent p-6 pt-20">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-green-300">
                  Chamber studies, often months of waiting
                </p>
              </div>
            </div>
          </FadeIn>
        </div>

        <div className="space-y-16 lg:col-span-7 lg:pl-6">
          {BLOCKS.map((b, i) => (
            <FadeIn key={b.n} delay={i * 0.08}>
              <article className="border-l-2 border-purple-600/40 pl-8 transition-colors duration-500 hover:border-green-400" data-testid={`problem-block-${b.n}`}>
                <p className="font-mono text-xs tracking-[0.3em] text-purple-400">{b.n}</p>
                <h3 className="mt-3 font-display text-xl font-semibold text-slate-50 sm:text-2xl">
                  {b.title}
                </h3>
                {b.body.map((p, j) => (
                  <p key={j} className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
                    {p}
                  </p>
                ))}
              </article>
            </FadeIn>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default Problem;
